import { gsap } from '../animations';

/**
 * Animazioni e comportamenti del template single-tm_offerta.php:
 * - Hero: reveal stagger testo + immagine.
 * - Pacchetto: reveal stagger righe sistemazioni.
 * - Trust: reveal stagger pillar e gallery.
 * - Sticky booking bar: nascosta con IntersectionObserver quando #prenota
 *   è visibile (CTA già in schermo → la bar è ridondante).
 * Con reduced-motion: niente gsap.from; la sticky bar rimane visibile
 * ma comunque senza animazione (solo show/hide).
 */
export function initSingleOfferta() {
	const hero = document.querySelector( '[data-tm-offerta-hero]' );
	if ( ! hero ) {
		return;
	}

	const reducedMotion = window.matchMedia( '(prefers-reduced-motion: reduce)' ).matches;

	// ── Sticky booking bar ────────────────────────────────────────────────────
	const bar    = document.querySelector( '[data-tm-sticky-bar]' );
	const prenota = document.getElementById( 'prenota' );

	if ( bar && prenota ) {
		// Bar inizialmente visibile (aria-hidden="true" nel PHP per gli screen reader;
		// lo togliamo qui quando la bar è effettivamente visibile nella pagina).
		const showBar = () => {
			bar.removeAttribute( 'aria-hidden' );
			// Fade-in rapido: feedback immediato quando la CTA principale esce dallo schermo.
			bar.style.transition = reducedMotion ? 'none' : 'opacity 0.25s ease, transform 0.25s ease';
			bar.style.opacity    = '1';
			bar.style.transform  = 'translateY(0)';
			bar.style.pointerEvents = '';
		};

		const hideBar = () => {
			bar.setAttribute( 'aria-hidden', 'true' );
			bar.style.transition = reducedMotion ? 'none' : 'opacity 0.2s ease, transform 0.2s ease';
			bar.style.opacity    = '0';
			bar.style.transform  = 'translateY(100%)';
			bar.style.pointerEvents = 'none';
		};

		// Stato iniziale: nascosta (la hero CTA è above the fold).
		bar.style.opacity       = '0';
		bar.style.transform     = 'translateY(100%)';
		bar.style.pointerEvents = 'none';
		bar.setAttribute( 'aria-hidden', 'true' );

		// La bar è utile solo quando NESSUNA delle due CTA primarie è in vista:
		// non la hero ("Prenota ora" above the fold) né le sistemazioni (#prenota).
		// Osserviamo entrambe per non mostrarla sopra la hero al caricamento.
		let heroVisible    = true;
		let prenotaVisible = false;
		const sync = () => ( heroVisible || prenotaVisible ) ? hideBar() : showBar();

		new IntersectionObserver(
			( entries ) => { heroVisible = entries[ 0 ].isIntersecting; sync(); },
			{ threshold: 0 }
		).observe( hero );

		new IntersectionObserver(
			( entries ) => { prenotaVisible = entries[ 0 ].isIntersecting; sync(); },
			// rootMargin negativo: scatta un po' prima che #prenota tocchi il bordo.
			{ rootMargin: '0px 0px -40px 0px', threshold: 0 }
		).observe( prenota );
	}

	// Resto delle animazioni richiede reduced-motion OFF.
	if ( reducedMotion ) {
		return;
	}

	// ── Hero: testo e immagine ───────────────────────────────────────────────
	const heroText = hero.querySelector( '[data-tm-offerta-text]' );
	const heroImg  = hero.querySelector( '[data-tm-offerta-img]' );

	if ( heroText && heroText.children.length ) {
		gsap.from( heroText.children, {
			y: 28,
			opacity: 0,
			duration: 0.75,
			ease: 'power3.out',
			stagger: 0.09,
			scrollTrigger: { trigger: heroText, start: 'top 88%', once: true },
		} );
	}

	if ( heroImg ) {
		gsap.from( heroImg, {
			y: 20,
			opacity: 0,
			duration: 0.8,
			ease: 'power3.out',
			delay: 0.15,
			scrollTrigger: { trigger: heroImg, start: 'top 88%', once: true },
		} );
	}

	// ── Pacchetto: righe sistemazioni ────────────────────────────────────────
	const pacchetto = document.querySelector( '[data-tm-offerta-pacchetto]' );
	if ( pacchetto ) {
		const sistRows = pacchetto.querySelectorAll( '.tm-sist__row' );
		if ( sistRows.length ) {
			gsap.from( sistRows, {
				y: 20,
				opacity: 0,
				duration: 0.55,
				ease: 'power3.out',
				stagger: 0.07,
				scrollTrigger: { trigger: sistRows[0], start: 'top 85%', once: true },
			} );
		}
	}

	// ── Trust: pillar cards e gallery ────────────────────────────────────────
	const trust = document.querySelector( '[data-tm-offerta-trust]' );
	if ( trust ) {
		const pillarItems = trust.querySelectorAll( '[data-tm-trust-item]' );
		if ( pillarItems.length ) {
			gsap.from( pillarItems, {
				y: 32,
				opacity: 0,
				duration: 0.7,
				ease: 'power3.out',
				stagger: 0.1,
				scrollTrigger: { trigger: trust.querySelector( '[data-tm-trust-pillar]' ), start: 'top 80%', once: true },
			} );
		}

		const galleryItems = trust.querySelectorAll( '[data-tm-trust-gallery] li' );
		if ( galleryItems.length ) {
			gsap.from( galleryItems, {
				opacity: 0,
				scale: 0.94,
				duration: 0.5,
				ease: 'power2.out',
				stagger: 0.05,
				scrollTrigger: { trigger: trust.querySelector( '[data-tm-trust-gallery]' ), start: 'top 82%', once: true },
			} );
		}
	}
}
